import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./GenerateBlog.css";
import { regenerateBlogOutline } from "../../utils/blog";
import { saveBlogDraftV2, handleShare } from "../../utils/blog";
import RegenerateModal from "../../components/Common/RegenerateModal";
import SectionForm from "./SectionForm";
import axiosInstance from "../../utils/api";
import AsyncSelect from "react-select/async";

const GenerateBlog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { formData, blogOutline, originalApiResponse, isEditMode } = location.state || {};

  // Form state initialized with data from previous screen
  const [form, setForm] = useState({
    topic: formData?.topic || formData?.about || "",
    tone: formData?.tone || "casual",
    word_limit: formData?.word_limit || "",
    location: formData?.location || "",
    language: formData?.language || "English",
    semantic_clusters: formData?.semantic_clusters || [],
    session_id: formData?.session_id || "",
    agent_id: formData?.agent_id || "",
    uid: formData?.uid || "",
    brand_context: formData?.brand_context || "",
    exclude_topics: formData?.exclude_topics || "",
    cta_mode: formData?.cta_mode || "",
    cta_product_name: formData?.cta_product_name || "",
    cta_url: formData?.cta_url || "",
    faq_count: formData?.faq_count || "",
    blog_type: formData?.blog_type || "",
    format: formData?.format || "html",
  });

  const [showLoader, setShowLoader] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [blogId, setBlogId] = useState(null);
  const [payload, setPayload] = useState(null);
  const [allLocations, setAllLocations] = useState([]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/listing?type=country');
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const locationOptions = response.data.data.map(item => ({
            value: item.name,
            label: item.name,
          }));
          setAllLocations(locationOptions);
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };
    fetchLocations();
  }, []);

  const loadLocationOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    const filteredLocations = allLocations.filter(location =>
      location.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filteredLocations);
  };

  // State for editable blog outline
  const parsedOutline =
    typeof blogOutline === "string" ? JSON.parse(blogOutline) : blogOutline;
  const [blogOutlines, setBlogOutlines] = useState(() => {
    if (!parsedOutline) return [];
    if (Array.isArray(parsedOutline)) return parsedOutline;
    if (typeof parsedOutline === "object") return [parsedOutline];
    return [];
  });

  // State to track the original blog outline and any new outlines added
  const [currentBlogOutline, setCurrentBlogOutline] = useState(blogOutline);
  
  // Store original API response for merging
  const [originalResponse, setOriginalResponse] = useState(originalApiResponse);

  // Sync currentBlogOutline with blogOutlines when sections are deleted
  useEffect(() => {
    if (blogOutlines.length > 0) {
      setCurrentBlogOutline(blogOutlines);
    }
  }, [blogOutlines]);
  
  // Function to merge transformed sections back into original API structure
  const mergeOutlineWithOriginal = () => {
    if (!originalResponse || !currentBlogOutline) {
      // Fallback to current outline if no original response
      return currentBlogOutline;
    }
    
    // Deep clone the original response
    const merged = JSON.parse(JSON.stringify(originalResponse));
    
    // Get the transformed sections from currentBlogOutline
    let transformedSections = [];
    if (Array.isArray(currentBlogOutline)) {
      // If array, get sections from first outline
      transformedSections = currentBlogOutline[0]?.h2_sections || [];
    } else if (currentBlogOutline.h2_sections) {
      transformedSections = currentBlogOutline.h2_sections;
    }
    
    // Transform back to original API format
    const originalFormatSections = transformedSections.map((transformedSection, index) => {
      // Get original section by index (preserves order) or by h2 title
      const originalSection = merged.outline?.sections?.[index] || 
        merged.outline?.sections?.find(
          (s) => s.h2 === transformedSection.section_title
        ) || {};
      
      // Merge transformed data back into original structure
      // Only update fields that exist in transformed section, preserve original for others
      return {
        ...originalSection,
        h2: transformedSection.section_title || originalSection.h2,
        content_notes: transformedSection.content_scope !== undefined 
          ? transformedSection.content_scope 
          : originalSection.content_notes,
        // Preserve original seo_targets structure, only update if focus_keywords were explicitly changed
        seo_targets: originalSection.seo_targets || {
          primary: [],
          secondary: [],
          variants: [],
          questions: []
        },
        intent: transformedSection.intent !== undefined 
          ? transformedSection.intent 
          : originalSection.intent,
        // Update h3_blocks if they exist in transformed section
        h3_blocks: transformedSection.h3_blocks !== undefined 
          ? transformedSection.h3_blocks 
          : (originalSection.h3_blocks || []),
        // Preserve all other original fields
        priority: originalSection.priority,
        cta_block: originalSection.cta_block,
      };
    });
    
    // Update the merged response with transformed sections
    if (merged.outline) {
      merged.outline.sections = originalFormatSections;
      // Update h1_title if it was changed
      if (Array.isArray(currentBlogOutline) && currentBlogOutline[0]?.h1_title) {
        merged.outline.h1_title = currentBlogOutline[0].h1_title;
      } else if (currentBlogOutline?.h1_title) {
        merged.outline.h1_title = currentBlogOutline.h1_title;
      }
    }
    
    return merged;
  };

  const [isEditingOutlineIdx, setIsEditingOutlineIdx] = useState(null);
  const [isAddingOutline, setIsAddingOutline] = useState(false);
  const [editableH1Title, setEditableH1Title] = useState("");
  const [editableH2Sections, setEditableH2Sections] = useState([]);

  // Section-wise editing state
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const [sectionEditDraft, setSectionEditDraft] = useState(null);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [regenerateContext, setRegenerateContext] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDeleteSection = (outlineIdx, sectionIdx) => {
    const newOutline = blogOutlines[outlineIdx].h2_sections.filter(
      (_, index) => index !== sectionIdx
    );
    const newBlogOutlines = [...blogOutlines];
    newBlogOutlines[outlineIdx] = {
      ...newBlogOutlines[outlineIdx],
      h2_sections: newOutline,
    };
    setBlogOutlines(newBlogOutlines);
    setCurrentBlogOutline((prev) => {
      if (Array.isArray(prev)) {
        return prev.map((outline, i) => {
          if (i === outlineIdx) {
            return { ...outline, h2_sections: newOutline };
          }
          return outline;
        });
      }
      return prev;
    });
  };

  // Handler for "Regenerate text" button
  const handleRegenerateCurrentOutline = (idx) => {
    const type = idx === 0 ? "all" : "outline";
    setRegenerateContext({ type: type, index: idx });
    setIsRegenerateModalOpen(true);
  };

  // Edit outline
  const handleEditOutline = (idx) => {
    if (blogOutlines[idx]) {
      setEditableH1Title(blogOutlines[idx].h1_title);
      setEditableH2Sections([...blogOutlines[idx].h2_sections]);
      setIsEditingOutlineIdx(idx);
    }
  };

  const handleCancelEditOutline = () => {
    setIsEditingOutlineIdx(null);
  };

  // Delete outline
  const handleDeleteOutline = (idx) => {
    setBlogOutlines((prev) => prev.filter((_, i) => i !== idx));
    setCurrentBlogOutline((prev) => {
      if (Array.isArray(prev)) {
        return prev.filter((_, i) => i !== idx);
      }
      return prev;
    });
    setIsEditingOutlineIdx(null);
  };

  // Save edits for a specific outline
  const handleSaveOutlineEdits = () => {
    const updatedOutline = {
      h1_title: editableH1Title,
      h2_sections: [...editableH2Sections],
    };

    setBlogOutlines((prev) =>
      prev.map((outline, idx) =>
        idx === isEditingOutlineIdx ? updatedOutline : outline
      )
    );
    setCurrentBlogOutline((prev) => {
      if (Array.isArray(prev)) {
        return prev.map((outline, idx) =>
          idx === isEditingOutlineIdx ? updatedOutline : outline
        );
      }
      return prev;
    });

    setIsEditingOutlineIdx(null);
    toast.success("Outline updated successfully!");
  };

  const handleEditableH1TitleChange = (e) => {
    setEditableH1Title(e.target.value);
  };

  // --- Section Editing Block ---
  const handleEditableH2SectionChange = (field, value, index) => {
    const updatedSections = [...editableH2Sections];
    updatedSections[index] = { ...updatedSections[index], [field]: value };
    setEditableH2Sections(updatedSections);
  };

  const handleAddH2Section = () => {
    setEditableH2Sections([
      ...editableH2Sections,
      {
        section_title: "",
        content_scope: "",
        focus_keywords: [],
        intent: "",
        word_target: 0,
        format_type: "",
      },
    ]);
  };

  const handleDeleteH2Section = (index) => {
    const updatedSections = editableH2Sections.filter((_, i) => i !== index);
    setEditableH2Sections(updatedSections);
  };

  // Add new sections handlers
  const handleAddNewOutline = () => {
    setEditableH2Sections([
      {
        section_title: "",
        content_scope: "",
        focus_keywords: [],
        intent: "",
        word_target: 0,
        format_type: "",
      },
    ]);
    setIsAddingOutline(true);
  };

  const handleSaveNewOutline = () => {
    const newSections = [...editableH2Sections];

    setBlogOutlines((prev) => {
      if (prev.length > 0) {
        // Merge new sections with the first existing outline
        const updatedOutlines = [...prev];
        updatedOutlines[0] = {
          ...updatedOutlines[0],
          h2_sections: [...newSections, ...updatedOutlines[0].h2_sections],
        };
        return updatedOutlines;
      } else {
        // If no existing outlines, we shouldn't reach here, but handle gracefully
        toast.error("No existing outline found to add sections to.");
        return prev;
      }
    });

    setCurrentBlogOutline((prev) => {
      if (Array.isArray(prev) && prev.length > 0) {
        const updatedOutlines = [...prev];
        updatedOutlines[0] = {
          ...updatedOutlines[0],
          h2_sections: [...newSections, ...updatedOutlines[0].h2_sections],
        };
        return updatedOutlines;
      } else if (prev && typeof prev === "object" && !Array.isArray(prev)) {
        return {
          ...prev,
          h2_sections: [...newSections, ...(prev.h2_sections || [])],
        };
      } else {
        toast.error("No existing outline found to add sections to.");
        return prev;
      }
    });

    setIsAddingOutline(false);
    toast.success("New sections added successfully!");
  };

  const handleCancelAddOutline = () => {
    setIsAddingOutline(false);
  };

  // Generate final blog content
  const handleGenerateBlog = () => {
    // Merge transformed sections back into original API structure
    const mergedOutline = mergeOutlineWithOriginal();
    
    // Prepare the new payload structure for the new API
    const topicMapJson = typeof mergedOutline === 'string' 
      ? mergedOutline 
      : JSON.stringify(mergedOutline || {});
    
    console.log("handleGenerateBlog - form.format:", form.format);
    console.log("handleGenerateBlog - formData?.format:", formData?.format);
    
    setPayload({
      topic: form.topic,
      tone: form.tone,
      wordCountTarget: form.word_limit,
      location: form.location,
      language: form.language,
      semanticClusters: form.semantic_clusters || [],
      brandContext: form.brand_context || "",
      excludeTopics: form.exclude_topics || "",
      ctaMode: form.cta_mode || "",
      ctaProductName: form.cta_product_name || "",
      ctaUrl: form.cta_url || "",
      faqCount: form.faq_count || "",
      topicMapJson: topicMapJson,
      blogType: form.blog_type || "",
      sessionId: form.session_id,
      agentId: form.agent_id,
      uid: form.uid,
      query: form.topic,
      format: form.format || "html",
    });
    navigate("/skills/blog/blog-playground", {
      state: { payload: null, formData: form, blogOutline: currentBlogOutline, newPayload: {
        topic: form.topic,
        tone: form.tone,
        wordCountTarget: form.word_limit,
        location: form.location,
        language: form.language,
        semanticClusters: form.semantic_clusters || [],
        brandContext: form.brand_context || "",
        excludeTopics: form.exclude_topics || "",
        ctaMode: form.cta_mode || "",
        ctaProductName: form.cta_product_name || "",
        ctaUrl: form.cta_url || "",
        faqCount: form.faq_count || "",
        topicMapJson: topicMapJson,
        blogType: form.blog_type || "",
        sessionId: form.session_id,
        agentId: form.agent_id,
        uid: form.uid,
        query: form.topic,
        format: form.format || "html",
      } },
    });
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    const draftDataToSave = {
      name: form.about?.slice(0, 40) || "Blog Draft",
      tone: form.tone,
      about: form.about,
      word_limit: form.word_limit,
      location: form.location,
      language: form.language,
      further_details: form.further_details,
      status: "draft",
      images: [],
      image_names: [],
      keyword_clusters: JSON.stringify(form.semantic_clusters) || "",
      outlines: currentBlogOutline ? JSON.stringify(currentBlogOutline) : "",
      final_blog: "",
      session_id: form.session_id,
      agent_id: form.agent_id,
      uid: form.uid,
    };

    try {
      const result = await saveBlogDraftV2(draftDataToSave);
      setBlogId(result.blog?.id || result.blog?._id);
      toast.success("Draft saved successfully!");
    } catch (err) {
      console.error("Failed to save draft:", err);
      let errorMessage = "Unknown error";
      
      // Handle different error response formats
      if (err.response && err.response.data) {
        // Handle axios-style error responses
        const errorData = err.response.data;
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.join(", ");
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = JSON.stringify(errorData);
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      } else if (err && typeof err.text === "function") {
        try {
          const errorText = await err.text();
          const parsedError = JSON.parse(errorText);
          if (parsedError.errors && Array.isArray(parsedError.errors)) {
            errorMessage = parsedError.errors.join(", ");
          } else {
            errorMessage = errorText;
          }
        } catch (textErr) {
          errorMessage = "Failed to parse API error response.";
        }
      } else {
        try {
          errorMessage = JSON.stringify(err);
        } catch (stringifyErr) {}
      }
      toast.error(`Failed to save draft: ${errorMessage}`);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleEditSection = (section, idx) => {
    setEditingSectionIdx(idx);
    const sectionData = { ...section };
    // Ensure h3_blocks array exists
    if (!sectionData.h3_blocks) {
      sectionData.h3_blocks = [];
    }
    setSectionEditDraft(sectionData);
  };

  const handleCancelSectionEdit = () => {
    setEditingSectionIdx(null);
    setSectionEditDraft(null);
  };

  const handleSectionEditChange = (field, value) => {
    setSectionEditDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddH3 = () => {
    if (!sectionEditDraft) return;
    setSectionEditDraft({
      ...sectionEditDraft,
      h3_blocks: [...(sectionEditDraft.h3_blocks || []), { h3: "", hints: [] }]
    });
  };

  const handleRemoveH3 = (h3Index) => {
    if (!sectionEditDraft) return;
    setSectionEditDraft({
      ...sectionEditDraft,
      h3_blocks: (sectionEditDraft.h3_blocks || []).filter((_, idx) => idx !== h3Index)
    });
  };

  const handleUpdateH3 = (h3Index, field, value) => {
    if (!sectionEditDraft) return;
    const updatedH3s = [...(sectionEditDraft.h3_blocks || [])];
    updatedH3s[h3Index] = { ...updatedH3s[h3Index], [field]: value };
    setSectionEditDraft({ ...sectionEditDraft, h3_blocks: updatedH3s });
  };

  const handleAddHint = (h3Index) => {
    if (!sectionEditDraft) return;
    const updatedH3s = [...(sectionEditDraft.h3_blocks || [])];
    updatedH3s[h3Index] = {
      ...updatedH3s[h3Index],
      hints: [...(updatedH3s[h3Index].hints || []), ""]
    };
    setSectionEditDraft({ ...sectionEditDraft, h3_blocks: updatedH3s });
  };

  const handleRemoveHint = (h3Index, hintIndex) => {
    if (!sectionEditDraft) return;
    const updatedH3s = [...(sectionEditDraft.h3_blocks || [])];
    updatedH3s[h3Index] = {
      ...updatedH3s[h3Index],
      hints: (updatedH3s[h3Index].hints || []).filter((_, idx) => idx !== hintIndex)
    };
    setSectionEditDraft({ ...sectionEditDraft, h3_blocks: updatedH3s });
  };

  const handleUpdateHint = (h3Index, hintIndex, value) => {
    if (!sectionEditDraft) return;
    const updatedH3s = [...(sectionEditDraft.h3_blocks || [])];
    updatedH3s[h3Index] = {
      ...updatedH3s[h3Index],
      hints: (updatedH3s[h3Index].hints || []).map((hint, idx) => idx === hintIndex ? value : hint)
    };
    setSectionEditDraft({ ...sectionEditDraft, h3_blocks: updatedH3s });
  };

  const handleSaveSectionEdit = (outlineIdx) => {
    // Ensure h3_blocks are preserved in the section data
    const sectionToSave = { ...sectionEditDraft };
    if (!sectionToSave.h3_blocks) {
      sectionToSave.h3_blocks = [];
    }
    
    setBlogOutlines((prev) =>
      prev.map((outline, idx) => {
        if (idx !== outlineIdx || !outline) return outline;
        const updatedSections = [...outline.h2_sections];
        updatedSections[editingSectionIdx] = sectionToSave;
        return { ...outline, h2_sections: updatedSections };
      })
    );
    setCurrentBlogOutline((prev) => {
      if (Array.isArray(prev)) {
        return prev.map((outline, idx) => {
          if (idx !== outlineIdx) return outline;
          const updatedSections = [...outline.h2_sections];
          updatedSections[editingSectionIdx] = sectionToSave;
          return { ...outline, h2_sections: updatedSections };
        });
      }
      return prev;
    });
    setEditingSectionIdx(null);
    setSectionEditDraft(null);
    toast.success("Section updated successfully!");
  };

  const handleRegenerateSectionText = (outlineIdx, sectionIdx) => {
    setRegenerateContext({
      type: "section",
      outlineIndex: outlineIdx,
      sectionIndex: sectionIdx,
    });
    setIsRegenerateModalOpen(true);
  };

  const handleRegenerateSubmit = async (customPrompt) => {
    if (!regenerateContext) return;

    setShowLoader(true);
    setIsRegenerateModalOpen(false);

    const { type, index, outlineIndex, sectionIndex } = regenerateContext;
    const details = customPrompt || form.further_details;

    try {
      if (type === "all") {
        const regenerationPromises = blogOutlines.map((outline, i) => {
          const semanticClusterForOutline = form.semantic_clusters[i]
            ? [form.semantic_clusters[i]]
            : [];
          return regenerateBlogOutline(
            form.about,
            form.tone,
            form.word_limit,
            form.location,
            form.language,
            details,
            semanticClusterForOutline,
            form.session_id,
            form.agent_id,
            form.uid,
            outline,
            undefined
          );
        });
        const newOutlines = await Promise.all(regenerationPromises);
        const extractedOutlines = newOutlines.map(
          (outline) => outline?.topic_map || outline
        );
        setBlogOutlines(extractedOutlines);
        setCurrentBlogOutline(extractedOutlines);
        toast.success("All outlines regenerated successfully!");

        const payload = {
          request_type: "blog",
          additional_data: {
            agent_id: form.agent_id,
            session_id: form.session_id,
            uid: form.uid,
            step: 3,
            topic_map: extractedOutlines,
            tone_of_voice: form.tone,
            further_details: details,
            blog_length: form.word_limit,
            location_focus: form.location,
            language: form.language,
            blog_about: form.about,
            streaming: true,
            brand_context: form.brand_context,
          },
        };
        setPayload(payload);
        navigate("/skills/blog/blog-playground", {
          state: { payload, formData: form, blogOutline: extractedOutlines },
        });
      } else if (type === "outline") {
        const previousOutline = blogOutlines[index];
        const semanticClusterForOutline = form.semantic_clusters[index];
        const newOutlineResponse = await regenerateBlogOutline(
          form.about,
          form.tone,
          form.word_limit,
          form.location,
          form.language,
          details,
          semanticClusterForOutline ? [semanticClusterForOutline] : [],
          form.session_id,
          form.agent_id,
          form.uid,
          previousOutline,
          undefined
        );
        const newOutline = newOutlineResponse?.topic_map || newOutlineResponse;
        setBlogOutlines((prev) =>
          prev.map((outline, i) => (i === index ? newOutline : outline))
        );
        setCurrentBlogOutline((prev) => {
          if (Array.isArray(prev)) {
            return prev.map((outline, i) =>
              i === index ? newOutline : outline
            );
          }
          return prev;
        });
        toast.success("Outline regenerated successfully!");
      } else if (type === "section") {
        const previousOutline = blogOutlines[outlineIndex];
        if (!previousOutline) {
          toast.error("Cannot regenerate section: The outline does not exist.");
          return;
        }
        const semanticClusterForOutline = form.semantic_clusters[outlineIndex];
        const sectionToRegenerate = previousOutline.h2_sections[sectionIndex];
        const sectionParams = {
          section_name: sectionToRegenerate.section_title,
          previous_response: previousOutline,
        };

        const streamedData = await regenerateBlogOutline(
          form.about,
          form.tone,
          form.word_limit,
          form.location,
          form.language,
          details,
          semanticClusterForOutline ? [semanticClusterForOutline] : [],
          form.session_id,
          form.agent_id,
          form.uid,
          previousOutline,
          undefined,
          sectionParams
        );

        const newOutlineData = streamedData;

        if (
          !newOutlineData ||
          !newOutlineData.h2_sections ||
          !Array.isArray(newOutlineData.h2_sections) ||
          newOutlineData.h2_sections.length === 0
        ) {
          console.error(
            "Regenerate API response could not be parsed or missing h2_sections:",
            newOutlineData
          );
          toast.error(
            "Failed to parse regenerate response or missing sections."
          );
          return;
        }

        setBlogOutlines((prev) =>
          prev.map((outline, oIdx) => {
            if (oIdx !== outlineIndex) return outline;
            return { ...outline, h2_sections: newOutlineData.h2_sections };
          })
        );
        setCurrentBlogOutline((prev) => {
          if (Array.isArray(prev)) {
            return prev.map((outline, oIdx) => {
              if (oIdx !== outlineIndex) return outline;
              return { ...outline, h2_sections: newOutlineData.h2_sections };
            });
          }
          return prev;
        });

        toast.success(`Section regenerated successfully!`);
      }
    } catch (error) {
      console.error("An error occurred during regeneration:", error);
      toast.error("Failed to regenerate. Check console for details.");
    } finally {
      setShowLoader(false);
      setRegenerateContext(null);
    }
  };

  return (
    <div className="generate-blog-container" style={{ position: "relative" }}>
      <ToastContainer />
      {showLoader && (
        <div className="loader-overlay">
          <div className="loader-spinner">
            <span
              className="spinner-border"
              role="status"
              aria-hidden="true"
            ></span>
          </div>
        </div>
      )}

      <div className="breadcrumb-container mx-3">
        <div className="breadcrumb-back">
          <span className="back-text" onClick={() => navigate(-2)}>
            &lt; Back
          </span>
        </div>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/">Home</a>
            </li>
            <span className="breadcrumb-separator"> &gt; </span>
            <li className="breadcrumb-item">
              <a href="/skills">Skills</a>
            </li>
            <span className="breadcrumb-separator"> &gt; </span>
            <li className="breadcrumb-item">
              <a href="/skills/blog">Blog templates</a>
            </li>
            <span className="breadcrumb-separator"> &gt; </span>
            <li className="breadcrumb-item active">
              <span>Generate New Blog</span>
            </li>
          </ol>
        </nav>
      </div>

      <div className="d-flex justify-content-between mb-3">
        <div className="title-label">Generate New Blog</div>
        <div className="d-flex gap-2">
          <button className="btn bgprimary" onClick={() => handleShare(blogId)}>
            Share
          </button>
          <button
            className="btn btn-dark rounded"
            onClick={handleSaveDraft}
            disabled={isSavingDraft}
          >
            Save as draft
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <h2 className="form-title">
            Please fill further information as relevant
          </h2>
          <div className="info-display">
            <div className="info-group">
              <label>What is this blog about? *</label>
              <div className="info-text">{form.about}</div>
            </div>
            <div className="info-group">
              <label>What should be the tone of voice?</label>
              <div className="info-text">{form.tone}</div>
            </div>
            <div className="info-group">
              <label>How long do you want the blog to be?</label>
              <div className="info-text">{form.word_limit}</div>
            </div>
            {form.location && (
              <div className="info-group">
                <label>Location focus</label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadLocationOptions}
                  value={
                    form.location
                      ? { label: form.location, value: form.location }
                      : null
                  }
                  onChange={(selectedOption) => {
                    setForm((prevForm) => ({
                      ...prevForm,
                      location: selectedOption ? selectedOption.value : "",
                    }));
                  }}
                  isClearable
                  isSearchable
                  placeholder="Type to search location..."
                  noOptionsMessage={({ inputValue }) =>
                    !inputValue
                      ? "Type to search..."
                      : "No locations found for that query"
                  }
                  loadingMessage={() => "Searching locations..."}
                />
              </div>
            )}
            {form.language && (
              <div className="info-group">
                <label>Language</label>
                <div className="info-text">{form.language}</div>
              </div>
            )}
            {form.further_details && (
              <div className="info-group">
                <label>Further details</label>
                <div className="info-text">{form.further_details}</div>
              </div>
            )}
          </div>
          <button className="optimize-button mt-2" onClick={handleGenerateBlog}>
            Generate blog
          </button>
          <button type="button" className="documents-button">
            <i className="fas fa-plus"></i> Any additional documents
          </button>
        </div>

        <div className="col-md-8  border-start">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="form-title" style={{ marginBottom: 0 }}>
              Blog Outline
            </h2>
            <button
              className="btn bgprimary"
              onClick={handleAddNewOutline}
              disabled={isAddingOutline}
            >
              + Add New Sections
            </button>
          </div>

          {blogOutlines && blogOutlines.length > 0 && (
            <div className="outline-content">
              {isAddingOutline && (
                <div
                  className="outline-section-item"
                  style={{ position: "relative" }}
                >
                  {Array.isArray(editableH2Sections) && (
                    <div className="section-content">
                      {editableH2Sections.map((section, idx) => (
                        <SectionForm
                          key={idx}
                          section={section}
                          onChange={(field, value) =>
                            handleEditableH2SectionChange(field, value, idx)
                          }
                          onDelete={handleDeleteH2Section}
                          showDelete={true}
                          index={idx}
                        />
                      ))}
                      <div className="bottom-actions">
                        <button
                          onClick={() =>
                            setEditableH2Sections([
                              ...editableH2Sections,
                              {
                                section_title: "",
                                content_scope: "",
                                focus_keywords: [],
                                intent: "",
                                word_target: 0,
                                format_type: "",
                              },
                            ])
                          }
                          className="btn bgprimary"
                        >
                          Add New Section
                        </button>
                        <button
                          onClick={handleSaveNewOutline}
                          className="btn bgprimary"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelAddOutline}
                          className="btn btn-outline-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {blogOutlines.map((outline, idx) => (
                <div
                  className="outline-section-item"
                  key={idx}
                  style={{ position: "relative" }}
                >
                  {/* {isEditingOutlineIdx !== idx && (
                    <button
                      onClick={() => handleDeleteOutline(idx)}
                      className="delete-outline-button"
                      title="Delete Outline"
                    >
                      ×
                    </button>
                  )} */}

                  {isEditingOutlineIdx === idx ? (
                    <>
                      <div className="bottom-actions">
                        <button
                          onClick={handleCancelEditOutline}
                          className="btn btn-outline-secondary"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveOutlineEdits}
                          className="btn bgprimary"
                        >
                          Save
                        </button>
                      </div>
                      <div className="outline-title">
                        <input
                          type="text"
                          value={editableH1Title}
                          onChange={handleEditableH1TitleChange}
                          className="form-input form-control"
                        />
                      </div>
                      {Array.isArray(editableH2Sections) &&
                        editableH2Sections.length > 0 && (
                          <div className="section-content">
                            {editableH2Sections.map((section, sidx) => (
                              <SectionForm
                                key={sidx}
                                section={section}
                                onChange={(field, value) =>
                                  handleEditableH2SectionChange(
                                    field,
                                    value,
                                    sidx
                                  )
                                }
                                onDelete={handleDeleteH2Section}
                                showDelete={true}
                                index={sidx}
                              />
                            ))}
                            <button
                              onClick={handleAddH2Section}
                              className="btn bgprimary"
                            >
                              Add New Section
                            </button>
                          </div>
                        )}
                    </>
                  ) : (
                    <>
                      <div className="buttons-container">
                        {/* <button
                          onClick={() => handleRegenerateCurrentOutline(idx)}
                          className="outline-action-button"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16.862 4.487L18.549 2.799M18.549 2.799L20.236 4.487M18.549 2.799V7.901M7.9013 2.799H12.0003M12.0003 2.799V7.9003M21.2003 12C21.2003 16.971 17.0723 21.098 12.1013 21.098C7.1303 21.098 3.0033 16.971 3.0033 12C3.0033 7.029 7.1303 2.902 12.1013 2.902"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                            <path
                              d="M11.0003 16.0002H16.0003M13.5003 13.5002L13.5003 18.5002"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                          </svg>
                          Regenerate All Outlines
                        </button>
                        <button
                          onClick={() => handleEditOutline(idx)}
                          className="outline-action-button"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16.862 4.487L18.549 2.799M18.549 2.799L20.236 4.487M18.549 2.799V7.901M7.9013 2.799H12.0003M12.0003 2.799V7.9003M21.2003 12C21.2003 16.971 17.0723 21.098 12.1013 21.098C7.1303 21.098 3.0033 16.971 3.0033 12C3.0033 7.029 7.1303 2.902 12.1013 2.902"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                            <path
                              d="M11.0003 16.0002H16.0003M13.5003 13.5002L13.5003 18.5002"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                          </svg>
                          Edit text
                        </button> */}
                      </div>
                      <div className="outline-title">
                        <h4 style={{ textTransform: "capitalize" }}>
                          {outline.h1_title}
                        </h4>
                      </div>
                      {Array.isArray(outline.h2_sections) &&
                        outline.h2_sections.length > 0 && (
                          <div className="section-content">
                            {outline.h2_sections.map((section, sidx) => (
                              <div key={sidx} className="section-view-block ">
                                {editingSectionIdx === sidx ? (
                                  <>
                                  <div className="form-section">
                                     <label className="form-label">Section Title</label>
                                    <input
                                      type="text"
                                      value={
                                        sectionEditDraft.section_title || ""
                                      }
                                      onChange={(e) =>
                                        handleSectionEditChange(
                                          "section_title",
                                          e.target.value
                                        )
                                      }
                                      className="form-input form-control mb-1"
                                      placeholder="Section Title"
                                    />
                                  </div>
                                   <div className="form-section">
                                    <label className="form-label">Content Scope</label>
                                    <textarea
                                      value={
                                        sectionEditDraft.content_scope || ""
                                      }
                                      onChange={(e) =>
                                        handleSectionEditChange(
                                          "content_scope",
                                          e.target.value
                                        )
                                      }
                                      className="form-input form-control mb-1"
                                      placeholder="Content Scope"
                                    />
                                   </div>
                                   <div className="form-section">
                                      <label className="form-label">Focus Keywords</label>
                                    <input
                                      type="text"
                                      value={
                                        Array.isArray(
                                          sectionEditDraft.focus_keywords
                                        )
                                          ? sectionEditDraft.focus_keywords.join(
                                              ", "
                                            )
                                          : ""
                                      }
                                      onChange={(e) =>
                                        handleSectionEditChange(
                                          "focus_keywords",
                                          e.target.value
                                            .split(",")
                                            .map((s) => s.trim())
                                        )
                                      }
                                      className="form-input form-control mb-1"
                                      placeholder="Focus Keywords (comma separated)"
                                    />
                                   </div>
                                   <div className="form-section">
                                     <label className="form-label">Intent</label>
                                    <input
                                      type="text"
                                      value={sectionEditDraft.intent || ""}
                                      onChange={(e) =>
                                        handleSectionEditChange(
                                          "intent",
                                          e.target.value
                                        )
                                      }
                                      className="form-input form-control mb-1"
                                      placeholder="Intent"
                                    />
                                   </div>
                                   <div className="form-section">
                                     <label className="form-label">Word Target</label>
                                    <input
                                      type="number"
                                      value={sectionEditDraft.word_target || ""}
                                      onChange={(e) =>
                                        handleSectionEditChange(
                                          "word_target",
                                          Number(e.target.value)
                                        )
                                      }
                                      className="form-input form-control mb-1"
                                      placeholder="Word Target"
                                    />
                                   </div>
                                      <div className="form-section">
                                         <label className="form-label">Format Type</label>
                                    <input
                                      type="text"
                                      value={sectionEditDraft.format_type || ""}
                                      onChange={(e) =>
                                        handleSectionEditChange(
                                          "format_type",
                                          e.target.value
                                        )
                                      }
                                      className="form-input form-control mb-1"
                                      placeholder="Format Type"
                                    />
                                      </div>
                                      
                                      {/* H3 Blocks Section */}
                                      <div className="form-section" style={{ marginTop: "20px" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                                          <label className="form-label" style={{ margin: 0 }}>H3 Blocks</label>
                                          <button
                                            type="button"
                                            className="btn bgprimary"
                                            onClick={handleAddH3}
                                            style={{ padding: "6px 12px", fontSize: "14px" }}
                                          >
                                            <i className="fas fa-plus"></i> Add H3
                                          </button>
                                        </div>

                                        {(!sectionEditDraft.h3_blocks || sectionEditDraft.h3_blocks.length === 0) && (
                                          <div style={{ 
                                            padding: "12px", 
                                            background: "#f9fafb", 
                                            borderRadius: "6px", 
                                            border: "1px dashed #ddd",
                                            textAlign: "center",
                                            color: "#666",
                                            fontSize: "13px",
                                            marginBottom: "12px"
                                          }}>
                                            No H3 blocks added yet. Click "Add H3" to add one.
                                          </div>
                                        )}

                                        {sectionEditDraft.h3_blocks && sectionEditDraft.h3_blocks.length > 0 && (
                                          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                            {sectionEditDraft.h3_blocks.map((h3Item, h3Index) => (
                                              <div
                                                key={h3Index}
                                                style={{
                                                  border: "1px solid #e5e7eb",
                                                  borderRadius: "6px",
                                                  padding: "12px",
                                                  background: "#fff"
                                                }}
                                              >
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                                  <h6 style={{ margin: 0, fontSize: "14px", fontWeight: 600 }}>H3 #{h3Index + 1}</h6>
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm"
                                                    onClick={() => handleRemoveH3(h3Index)}
                                                    style={{
                                                      background: "none",
                                                      border: "none",
                                                      color: "#dc3545",
                                                      padding: "4px 8px",
                                                      fontSize: "12px"
                                                    }}
                                                  >
                                                    <i className="fas fa-times"></i> Remove
                                                  </button>
                                                </div>

                                                <div style={{ marginBottom: "8px" }}>
                                                  <label className="form-label" style={{ fontSize: "13px" }}>H3 Title</label>
                                                  <input
                                                    type="text"
                                                    value={h3Item.h3 || ""}
                                                    onChange={(e) => handleUpdateH3(h3Index, "h3", e.target.value)}
                                                    className="form-control"
                                                    placeholder="Enter H3 title"
                                                    style={{ fontSize: "13px" }}
                                                  />
                                                </div>

                                                <div>
                                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                                    <label className="form-label" style={{ fontSize: "13px", margin: 0 }}>Hints</label>
                                                    <button
                                                      type="button"
                                                      className="btn btn-sm"
                                                      onClick={() => handleAddHint(h3Index)}
                                                      style={{
                                                        background: "#f3f4f6",
                                                        border: "1px solid #d1d5db",
                                                        padding: "4px 8px",
                                                        fontSize: "11px"
                                                      }}
                                                    >
                                                      <i className="fas fa-plus"></i> Add Hint
                                                    </button>
                                                  </div>

                                                  {(!h3Item.hints || h3Item.hints.length === 0) && (
                                                    <div style={{
                                                      padding: "8px",
                                                      background: "#f9fafb",
                                                      borderRadius: "4px",
                                                      fontSize: "12px",
                                                      color: "#666",
                                                      fontStyle: "italic"
                                                    }}>
                                                      No hints added. Click "Add Hint" to add one.
                                                    </div>
                                                  )}

                                                  {h3Item.hints && h3Item.hints.length > 0 && (
                                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                                      {h3Item.hints.map((hint, hintIndex) => (
                                                        <div key={hintIndex} style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                                                          <input
                                                            type="text"
                                                            value={hint}
                                                            onChange={(e) => handleUpdateHint(h3Index, hintIndex, e.target.value)}
                                                            className="form-control"
                                                            placeholder={`Hint ${hintIndex + 1}`}
                                                            style={{ flex: 1, fontSize: "12px" }}
                                                          />
                                                          <button
                                                            type="button"
                                                            className="btn btn-sm"
                                                            onClick={() => handleRemoveHint(h3Index, hintIndex)}
                                                            style={{
                                                              background: "none",
                                                              border: "none",
                                                              color: "#dc3545",
                                                              padding: "4px 8px"
                                                            }}
                                                          >
                                                            <i className="fas fa-times"></i>
                                                          </button>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                  
                                    <div className="section-edit-buttons">
                                      <button
                                        className="btn bgprimary"
                                        onClick={() =>
                                          handleSaveSectionEdit(idx)
                                        }
                                      >
                                        Save
                                      </button>
                                      <button
                                        className="btn btn-outline-secondary"
                                        onClick={handleCancelSectionEdit}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="d-flex justify-content-between mb-3 align-items-center">
                                      <div className="col-6">
                                        <b>Title:</b> {section.section_title}
                                      </div>
                                      <div className="section-action-buttons col-auto">
                                        <button
                                          className="btn btn-outline-secondary"
                                          onClick={() =>
                                            handleRegenerateSectionText(
                                              idx,
                                              sidx
                                            )
                                          }
                                        >
                                          Regenerate Text
                                        </button>
                                        <button
                                          className="btn btn-outline-secondary"
                                          onClick={() =>
                                            handleEditSection(section, sidx)
                                          }
                                        >
                                          Edit
                                        </button>
                                        <button
                                          className="btn btn-outline-secondary"
                                          onClick={() =>
                                            handleDeleteSection(idx, sidx)
                                          }
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                    <div className="mb-2">
                                      <b>Scope:</b> {section.content_scope}
                                    </div>
                                    <div className="mb-2">
                                      <b>Keywords:</b>{" "}
                                      {Array.isArray(section.focus_keywords)
                                        ? section.focus_keywords.map(
                                            (kw, i) => <div key={i}>• {kw}</div>
                                          )
                                        : ""}
                                    </div>
                                    
                                    {/* Render h3_blocks and hints */}
                                    {Array.isArray(section.h3_blocks) && section.h3_blocks.length > 0 && (
                                      <div className="mb-2">
                                        <b>H3 Blocks:</b>
                                        <div style={{ marginTop: "8px", marginLeft: "16px" }}>
                                          {section.h3_blocks.map((h3Item, h3Index) => (
                                            <div key={h3Index} style={{ marginBottom: "12px" }}>
                                              <h5 style={{ 
                                                fontSize: "15px", 
                                                fontWeight: 600, 
                                                marginBottom: "4px",
                                                color: "#333"
                                              }}>
                                                {h3Item.h3 || h3Item.title || ""}
                                              </h5>
                                              {Array.isArray(h3Item.hints) && h3Item.hints.length > 0 && (
                                                <ul style={{ marginLeft: "20px", marginTop: "4px" }}>
                                                  {h3Item.hints.map((hint, hintIndex) => (
                                                    <li key={hintIndex} style={{ fontSize: "14px", marginBottom: "4px" }}>
                                                      {hint}
                                                    </li>
                                                  ))}
                                                </ul>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    <div className="mb-2">
                                      <b>Intent:</b> {section.intent}
                                    </div>
                                    <div className="mb-2">
                                      <b>Word Target:</b> {section.word_target}
                                    </div>
                                    <div className="mb-2">
                                      <b>Format:</b> {section.format_type}
                                    </div>
                                    
                                  </>
                                )}
                                 {sidx < outline.h2_sections.length - 1 && <hr />}
                              </div>
                            ))}
                          </div>
                        )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <RegenerateModal
        isOpen={isRegenerateModalOpen}
        onClose={() => setIsRegenerateModalOpen(false)}
        onSubmit={handleRegenerateSubmit}
      />
    </div>
  );
};

export default GenerateBlog;
